import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const getRedirectUrl = () => {
  const configuredAppUrl = import.meta.env.VITE_APP_URL?.trim();
  const baseUrl = configuredAppUrl && configuredAppUrl.length > 0
    ? configuredAppUrl.replace(/\/$/, '')
    : window.location.origin;

  return `${baseUrl}/auth/callback`;
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: any) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthProvider initializing...');
    console.log('Environment:', {
      isProd: import.meta.env.PROD,
      hostname: window.location.hostname,
      origin: window.location.origin
    });

    let mounted = true;

    // Initialize auth state
    const initializeAuth = async () => {
      try {
        console.log('Getting initial session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting initial session:', error);
        } else {
          console.log('Initial session:', session?.user?.email || 'No session');
        }

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event, session?.user?.email || 'No user');
      
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle user profile creation/update
        if (event === 'SIGNED_IN' && session?.user) {
          await handleUserProfile(session.user);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleUserProfile = async (user: User) => {
    try {
      console.log('Handling user profile for:', user.email);

      // The handle_new_user trigger creates the profile on signup.
      // This is a fallback in case the trigger didn't fire.
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('id')
        .eq('id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching user profile:', fetchError);
        return;
      }

      if (!existingUser) {
        console.log('Creating new user profile...');
        const { error } = await supabase
          .from('users')
          .insert({
            id: user.id,
            email: user.email!,
            name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || '',
          });

        if (error) {
          console.error('Error creating user profile:', error);
        } else {
          console.log('User profile created successfully');
        }
      } else {
        console.log('User profile already exists');
      }
    } catch (error) {
      console.error('Error handling user profile:', error);
    }
  };

  const signUp = async (email: string, password: string, userData?: any) => {
    try {
      console.log('Signing up user:', email);
      const redirectUrl = getRedirectUrl();
      console.log('Sign up confirmation redirect URL:', redirectUrl);

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: redirectUrl,
        },
      });
      return { error };
    } catch (err) {
      console.error('Sign up error:', err);
      return { error: { message: 'Network error during sign up' } as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Signing in user:', email);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (err) {
      console.error('Sign in error:', err);
      return { error: { message: 'Network error during sign in' } as AuthError };
    }
  };

  const signOut = async () => {
    try {
      console.log('Signing out user');
      // Explicitly clear local state first — don't rely on onAuthStateChange
      setUser(null);
      setSession(null);
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase signOut error:', error);
      }
      // Force clear any persisted session data
      localStorage.removeItem('sb-' + (import.meta.env.VITE_SUPABASE_URL || '').split('//')[1]?.split('.')[0] + '-auth-token');
      return { error };
    } catch (err) {
      console.error('Sign out error:', err);
      // Even on error, ensure user is cleared locally
      setUser(null);
      setSession(null);
      return { error: { message: 'Network error during sign out' } as AuthError };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = getRedirectUrl().replace('/auth/callback', '/auth/reset-password');
      console.log('Reset password redirect URL:', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      return { error };
    } catch (err) {
      console.error('Reset password error:', err);
      return { error: { message: 'Network error during password reset' } as AuthError };
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};